import { Box, InputAdornment } from '@material-ui/core';

import { ConditionalWrapper } from '@/components/shared/components/ConditionalWrapper';
import { CopyToClipboardButton } from '@/components/shared/components/CopyToClipboardButton';
import {
  CustomFormLabel,
  CustomFormLabelProps,
} from '@/components/shared/components/NewContextForms/shared/CustomFormLabel';
import { StyledTooltip } from '@/components/shared/components/StyledTooltip';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface CustomFormLabelWithAdornmentProps extends CustomFormLabelProps {
  value?: string | number;
  tooltipTitle?: string | number | JSX.Element | null;
  labelIcon?: string;
  labelIconPosition?: 'start' | 'end';
  copyButton?: boolean;
  labelSuffix?: string | number | JSX.Element;
  useInputAdornment?: boolean;
}

export const CustomFormLabelWithAdornment = ({
  value,
  label,
  strongTitle,
  labelDynamicDataTexts,
  tooltipTitle,
  labelIcon = tooltipTitle ? 'info' : undefined,
  labelIconPosition = 'end',
  copyButton,
  labelSuffix,
  useInputAdornment = true,
}: CustomFormLabelWithAdornmentProps) => {
  const { palette } = useCustomTheme();

  const labelContent = (
    <ConditionalWrapper
      condition={!!tooltipTitle}
      wrapper={(children) => (
        <StyledTooltip title={tooltipTitle as string} tooltipProps={{ placement: 'top' }}>
          {children}
        </StyledTooltip>
      )}
    >
      <Box
        display="flex"
        alignItems="center"
        minWidth={0}
        width="100%"
        overflow="hidden"
        pr={useInputAdornment ? 1 : 0}
        style={useInputAdornment ? { minWidth: '70px' } : undefined}
      >
        {labelIcon && labelIconPosition === 'start' && (
          <i
            className={'material-icons-outlined'}
            style={{ fontSize: 16, color: palette.grey[400], lineHeight: 1, paddingRight: 6, flexShrink: 0 }}
          >
            {labelIcon}
          </i>
        )}
        <Box minWidth={0} flex={1} overflow="hidden">
          <CustomFormLabel label={label} strongTitle={strongTitle} labelDynamicDataTexts={labelDynamicDataTexts} />
        </Box>
        {labelIcon && labelIconPosition === 'end' && (
          <i
            className={'material-icons-outlined'}
            style={{ fontSize: 16, color: palette.grey[400], lineHeight: 1, paddingLeft: 6, flexShrink: 0 }}
          >
            {labelIcon}
          </i>
        )}
        {labelSuffix}
        {copyButton && value && <CopyToClipboardButton value={value?.toString()} />}
      </Box>
    </ConditionalWrapper>
  );

  if (!useInputAdornment) {
    return labelContent;
  }

  return (
    <InputAdornment position="start" style={{ flexShrink: copyButton ? 999 : 2, overflow: 'hidden', height: 'auto' }}>
      {labelContent}
    </InputAdornment>
  );
};
